/**
 * The Teams tab once the game is running: a live readout of the whole field, plus
 * the overrides to unstick it.
 *
 * Replaces the setup view at `active`, because from that moment the admin's job
 * changes completely — not "who is on which team" but "why has nobody moved for
 * ten minutes". Answering that needs per-PLAYER detail the setup cards never
 * showed: is their client even connected, is their scanner blocked, are they
 * scanning, and are they standing on the right planet at all.
 *
 * Three data sources feed it:
 *  - `selectedGame` for teams, progress, map and logs — admins receive the
 *    UNMASKED snapshot, so rival fog-of-war is fully visible here and no extra
 *    fetching is needed;
 *  - `TRL_TEAM_ACTIVITY` for live scanner state (already emitted to admins);
 *  - `PLAYER_LOCATIONS` for planet/position, which is admin-only and therefore
 *    deliberately absent from the snapshot.
 *
 * Read-only once the game is over: the same readout is useful for a post-mortem,
 * but an override at that point could only corrupt a finished result.
 */

import { useState, useMemo, useEffect } from 'react';
import { ConfirmModal } from '../../../ui/ConfirmModal';
import {
  getTeamProgressLevel,
  TRL_STALEMATE_CLUE_KINDS,
  type Player,
  type TrlDisruptionRecord,
  type TrlMapDef,
  type TrlStalemateClueKind,
  type TrlTeam,
  type TrlTeamProgress,
} from '@swtorbit/shared-socket-events';
import type { TrlAdminActivityMap } from '../../../hooks/useTrlAdminActivity';
import type { PlayerLocationMap } from '@swtorbit/shared-react-hooks';
import { TeamRosterEditor } from './TeamCard';

/** How a team's scanner activity reads on the dashboard. */
const ACTIVITY_LABEL: Record<string, { label: string; className: string }> = {
  idle: { label: 'Idle', className: 'text-slate' },
  scanning: { label: 'Scanning', className: 'text-aqua-dark' },
  triangulating: { label: 'Triangulating', className: 'text-aqua-dark' },
  processing: { label: 'Processing', className: 'text-aqua-dark' },
  cancelled: { label: 'Cancelled', className: 'text-amber' },
  blocked: { label: 'Blocked', className: 'text-red' },
  // Derived server-side from the member's reported area vs their team's current
  // planet — the exact question the Position / Area columns below are read for.
  inactive: { label: 'Wrong planet', className: 'text-amber' },
};

/**
 * Compact relative age, e.g. "12s", "4m", "1h".
 *
 * Deliberately coarse: the question a dashboard answers is "is this fresh or
 * stale", not "how many seconds exactly".
 */
function formatAgo(timestamp: number | undefined): string {
  if (!timestamp) return '—';
  const seconds = Math.max(0, Math.round((Date.now() - timestamp) / 1000));
  if (seconds < 60) return `${seconds}s`;
  const minutes = Math.floor(seconds / 60);
  if (minutes < 60) return `${minutes}m`;
  return `${Math.floor(minutes / 60)}h`;
}

interface PendingConfirm {
  title: string;
  message: string;
  confirmText: string;
  variant: 'primary' | 'danger' | 'warning';
  run: () => void;
}

export interface TrlLiveDashboardProps {
  teams: TrlTeam[];
  progressByTeam: Map<string, TrlTeamProgress>;
  map: TrlMapDef | undefined;
  players: Player[];
  activity: TrlAdminActivityMap;
  locations: PlayerLocationMap;
  activeBlocks: Map<string, TrlDisruptionRecord>;
  playerNameById: (playerId: string) => string;
  nodeLabelById: (nodeId: string) => string;
  unassignedPlayers: { playerId: string; name: string }[];
  /** False once the game is over — the readout stays, the overrides go. */
  canOverride: boolean;
  onForceClear: (teamId: string, nodeId?: string) => void;
  onSetCurrentNode: (teamId: string, nodeId: string) => void;
  onMarkFinished: (teamId: string) => void;
  onRevealClue: (teamId: string, clue: TrlStalemateClueKind) => void;
  onResendSnapshot: (teamId: string) => void;
  onAssign: (teamId: string, playerId: string) => void;
  onRemove: (teamId: string, playerId: string) => void;
  onSetCaptain: (teamId: string, playerId: string) => void;
  onClearBlock: (playerId: string) => void;
}

export function TrlLiveDashboard(props: TrlLiveDashboardProps) {
  const { teams, progressByTeam, map, canOverride } = props;
  const [confirm, setConfirm] = useState<PendingConfirm | null>(null);

  // Relative timestamps age on their own; nothing else re-renders this often, and
  // a push-driven dashboard would otherwise show "0s ago" until the next event.
  const [, setTick] = useState(0);
  useEffect(() => {
    const id = setInterval(() => setTick((t) => t + 1), 1000);
    return () => clearInterval(id);
  }, []);

  /**
   * Teams ordered the way an admin reads the field: furthest ahead first, then by
   * score. Finished teams sink to the bottom — they need no attention.
   */
  const ordered = useMemo(() => {
    const levelOf = (team: TrlTeam) => {
      const p = progressByTeam.get(team.id);
      if (!p || !map) return 0;
      return getTeamProgressLevel(map.nodes, map.edges, p.clearedNodeIds);
    };
    return [...teams].sort((a, b) => {
      const aFin = progressByTeam.get(a.id)?.finished ? 1 : 0;
      const bFin = progressByTeam.get(b.id)?.finished ? 1 : 0;
      if (aFin !== bFin) return aFin - bFin;
      const byLevel = levelOf(b) - levelOf(a);
      return byLevel !== 0 ? byLevel : b.totalScore - a.totalScore;
    });
  }, [teams, progressByTeam, map]);

  const finishedCount = ordered.filter((t) => progressByTeam.get(t.id)?.finished).length;

  return (
    <>
      <div className="d-flex justify-content-between align-items-center gap-xs flex-wrap">
        <h2 className="m-0">📡 Live game</h2>
        <span className="fs-sm text-slate">
          {finishedCount} of {teams.length} team{teams.length === 1 ? '' : 's'} finished
        </span>
      </div>

      {teams.length === 0 ? (
        <p className="text-cyan">No teams in this game.</p>
      ) : (
        <div className="d-flex flex-column gap-xs">
          {ordered.map((team) => (
            <TeamRow
              key={team.id}
              team={team}
              progress={progressByTeam.get(team.id)}
              askConfirm={setConfirm}
              {...props}
            />
          ))}
        </div>
      )}

      {canOverride && confirm && (
        <ConfirmModal
          isOpen
          title={confirm.title}
          message={confirm.message}
          confirmText={confirm.confirmText}
          variant={confirm.variant}
          onCancel={() => setConfirm(null)}
          onConfirm={() => {
            confirm.run();
            setConfirm(null);
          }}
        />
      )}
    </>
  );
}

// ============================================================================
// One team
// ============================================================================

interface TeamRowProps extends TrlLiveDashboardProps {
  team: TrlTeam;
  progress: TrlTeamProgress | undefined;
  askConfirm: (pending: PendingConfirm) => void;
}

function TeamRow({
  team,
  progress,
  map,
  players,
  activity,
  locations,
  activeBlocks,
  playerNameById,
  nodeLabelById,
  unassignedPlayers,
  canOverride,
  askConfirm,
  onForceClear,
  onSetCurrentNode,
  onMarkFinished,
  onRevealClue,
  onResendSnapshot,
  onAssign,
  onRemove,
  onSetCaptain,
  onClearBlock,
}: TeamRowProps) {
  const [expanded, setExpanded] = useState(false);
  const [showRoster, setShowRoster] = useState(false);
  const [moveTo, setMoveTo] = useState('');

  const round = map && progress ? getTeamProgressLevel(map.nodes, map.edges, progress.clearedNodeIds) : 0;
  const currentNode = progress?.currentNodeId
    ? map?.nodes.find((n) => n.id === progress.currentNodeId)
    : undefined;

  /**
   * Where the team is, in the words an admin would use. The order matters:
   * finished and held are terminal states, and "choosing a branch" is the specific
   * case of having no current node that is NOT a fault.
   */
  const position = progress?.finished
    ? { icon: '🏁', text: 'Finished', className: 'text-green-dark' }
    : progress?.waitingAtCheckpoint
      ? { icon: '⚑', text: 'Held at checkpoint', className: 'text-amber' }
      : currentNode
        ? { icon: '🪐', text: currentNode.label, className: 'text-white' }
        : { icon: '⑂', text: 'Choosing a branch', className: 'text-aqua-dark' };

  const imagesFound = currentNode ? progress?.nodeImagesFound?.[currentNode.id] ?? 0 : 0;
  const imagesNeeded = currentNode?.imagesToClear ?? 0;

  const blockedCount = team.members.filter((m) => activeBlocks.has(m.playerId)).length;

  // Planets this team could be moved to: everything on the map it hasn't cleared.
  const moveOptions = useMemo(
    () => (map?.nodes ?? []).filter((n) => !progress?.clearedNodeIds.includes(n.id)),
    [map?.nodes, progress?.clearedNodeIds]
  );

  return (
    <div className="trl-live__row">
      <button
        type="button"
        className="trl-live__summary"
        aria-expanded={expanded}
        onClick={() => setExpanded((open) => !open)}
      >
        <span className="trl-live__dot" style={{ background: team.color ?? 'transparent' }} aria-hidden="true" />
        <span className="trl-live__name">{team.name}</span>
        <span className={`trl-live__position ${position.className}`}>
          {position.icon} {position.text}
        </span>
        {currentNode && imagesNeeded > 1 && (
          <span className="badge badge--sm" title="Images found on this planet">
            {imagesFound}/{imagesNeeded}
          </span>
        )}
        <span className="badge badge--sm" title="Rounds cleared">
          R{round}
        </span>
        <span className="badge badge--sm">{team.totalScore} pts</span>
        <span className="badge badge--sm" title="Team members">
          {team.members.length} 👤
        </span>
        {blockedCount > 0 && (
          <span className="badge badge--sm badge--red" title="Members with a blocked scanner">
            {blockedCount} blocked
          </span>
        )}
        <span className="trl-live__chevron" aria-hidden="true">
          {expanded ? '▾' : '▸'}
        </span>
      </button>

      {expanded && (
        <div className="trl-live__detail">
          <table className="trl-live__members">
            <thead>
              <tr>
                <th>Player</th>
                <th>Client</th>
                <th>Scanner</th>
                <th>Planet</th>
                <th>Last position</th>
                <th aria-label="Actions" />
              </tr>
            </thead>
            <tbody>
              {team.members.length === 0 ? (
                <tr>
                  <td colSpan={6} className="fs-sm text-slate">
                    No players assigned.
                  </td>
                </tr>
              ) : (
                team.members.map((member) => {
                  const player = players.find((p) => p.playerId === member.playerId);
                  const connected = player?.status === 'connected';
                  const act = activity[member.playerId] ?? 'idle';
                  const view = ACTIVITY_LABEL[act] ?? ACTIVITY_LABEL.idle;
                  const loc = locations[member.playerId];
                  const block = activeBlocks.get(member.playerId);
                  const isCaptain = team.captainPlayerId === member.playerId;

                  return (
                    <tr key={member.playerId}>
                      <td>
                        <span className="d-flex align-items-center gap-2xs">
                          {isCaptain && (
                            <span className="badge badge--sm" title="Team captain — makes the map selections">
                              ★
                            </span>
                          )}
                          {playerNameById(member.playerId)}
                        </span>
                      </td>
                      <td>
                        <span
                          className={`dot ${connected ? 'dot--green' : 'dot--slate'}`}
                          title={connected ? 'Client connected' : 'Client disconnected'}
                        />
                        <span className="fs-xs text-slate ms-2xs">
                          {connected ? 'Online' : 'Offline'}
                        </span>
                      </td>
                      <td className={view.className}>{view.label}</td>
                      <td title={loc ? `Zone id ${loc.areaId}` : 'No area reported yet'}>
                        {loc ? (
                          <>
                            {loc.areaName}
                            <span className="fs-xs text-slate"> · {formatAgo(loc.enteredAt)}</span>
                          </>
                        ) : (
                          <span className="text-slate">Unknown</span>
                        )}
                      </td>
                      <td className="fs-xs">
                        {loc ? (
                          <>
                            <span className="text-slate">
                              {loc.position.x}, {loc.position.y}, {loc.position.z}
                            </span>
                            <span className="text-slate"> · {formatAgo(loc.positionAt)}</span>
                          </>
                        ) : (
                          <span className="text-slate">—</span>
                        )}
                      </td>
                      <td>
                        {block && canOverride && (
                          <button
                            className="btn btn--amber btn--sm"
                            title={`Clear the block (disrupted by ${block.disruptorName})`}
                            onClick={() => onClearBlock(member.playerId)}
                          >
                            Unblock
                          </button>
                        )}
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>

          {canOverride && (
            <div className="trl-live__overrides">
              <button
                className="btn btn--amber btn--sm"
                disabled={!!progress?.finished || !progress?.currentNodeId}
                title={
                  progress?.currentNodeId
                    ? 'Clear this planet for the team — scores its natural rank, but never the treasure'
                    : 'The team has no active planet to clear'
                }
                onClick={() =>
                  askConfirm({
                    title: 'Finish this round for the team?',
                    message:
                      `${team.name} will clear ${currentNode?.label ?? 'their current planet'} and move on. ` +
                      'They score the points for whatever rank they land in, but they will NOT win the ' +
                      "planet's treasure even if they are first. This cannot be undone.",
                    confirmText: 'Finish round',
                    variant: 'warning',
                    run: () => onForceClear(team.id),
                  })
                }
              >
                ⏭ Finish round
              </button>

              <div className="d-flex align-items-center gap-2xs">
                <select
                  className="select"
                  value={moveTo}
                  disabled={!!progress?.finished}
                  onChange={(e) => setMoveTo(e.target.value)}
                  style={{ minWidth: '150px' }}
                >
                  <option value="">-- Move to planet --</option>
                  {moveOptions.map((n) => (
                    <option key={n.id} value={n.id}>
                      {n.label}
                    </option>
                  ))}
                </select>
                <button
                  className="btn btn--outline btn--slate btn--sm"
                  disabled={!moveTo}
                  onClick={() => {
                    const target = moveTo;
                    askConfirm({
                      title: 'Move this team?',
                      message: `${team.name} will be placed on ${nodeLabelById(target)} and it will be revealed to them.`,
                      confirmText: 'Move team',
                      variant: 'primary',
                      run: () => {
                        onSetCurrentNode(team.id, target);
                        setMoveTo('');
                      },
                    });
                  }}
                >
                  Move
                </button>
              </div>

              <div className="d-flex align-items-center gap-2xs">
                <span className="fs-xs text-slate">Reveal clue:</span>
                {TRL_STALEMATE_CLUE_KINDS.map((clue) => (
                  <button
                    key={clue}
                    className="btn btn--outline btn--slate btn--sm"
                    disabled={!progress?.currentNodeId}
                    title={`Unlock the ${clue} clue now instead of waiting for its timer`}
                    onClick={() => onRevealClue(team.id, clue)}
                  >
                    {clue}
                  </button>
                ))}
              </div>

              <button
                className="btn btn--outline btn--slate btn--sm"
                title="Re-push the game state to every client — fixes a client stuck on stale data"
                onClick={() => onResendSnapshot(team.id)}
              >
                🔄 Re-push state
              </button>

              <button
                className="btn btn--red btn--sm"
                disabled={!!progress?.finished}
                title="Finish this team outright so it stops holding up the field"
                onClick={() =>
                  askConfirm({
                    title: 'Mark this team finished?',
                    message:
                      `${team.name} will be marked as having completed the map. Use this for a team that ` +
                      'has dropped out — it stops them holding every other team at a checkpoint. ' +
                      'This cannot be undone.',
                    confirmText: 'Mark finished',
                    variant: 'danger',
                    run: () => onMarkFinished(team.id),
                  })
                }
              >
                🏁 Mark finished
              </button>

              <button
                className="btn btn--outline btn--slate btn--sm"
                aria-expanded={showRoster}
                onClick={() => setShowRoster((open) => !open)}
              >
                {showRoster ? 'Hide roster' : '👥 Edit roster'}
              </button>
            </div>
          )}

          {canOverride && showRoster && (
            <div className="d-flex flex-column gap-2xs mt-xs">
              <TeamRosterEditor
                team={team}
                playerNameById={playerNameById}
                unassignedPlayers={unassignedPlayers}
                activeBlocks={activeBlocks}
                onAssign={onAssign}
                onRemove={onRemove}
                onSetCaptain={onSetCaptain}
                onClearBlock={onClearBlock}
              />
            </div>
          )}
        </div>
      )}
    </div>
  );
}

export default TrlLiveDashboard;
